import React, { memo, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { VariableSizeGrid as Grid, GridChildComponentProps, areEqual } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import { Box, Flex, Text } from 'rebass'
import _ from 'lodash'
import ReviewAllTasks from './ReviewAllTasks'
import { isShowCardReviewComponent } from 'components/utils'

import {
    MAX_PAGE_SIZE,
    selectActivePageNum,
    selectSelectedCampaignUuid,
    selectSelectedSubmissionView,
    selectSidebarStep,
    selectSidebarTask,
    selectReviewPendingSubmission,
    selectSelectedCampaign,
    selectCheckCanReviewSubmissionByStep,
    selectSelectedCampaignSummary,
    selectSubmissionListParams,
    SubmissionProgressProps,
} from 'state/review'
import {
    clearSubmissionsList,
    getSubmissionsPageList,
    getBatchSubmissionsPageList,
    SubmissionState,
    SUBMISSION_VIEW_ENUM,
    selectBatchSubmissionsList,
    selectIsPendingSubmitSubmissionExist,
    cancelSubmissionPageList,
    cancelBatchSubmissionPageList,
} from 'state/submissions'
import EmptyState from '../EmptyState'
import { selectRequestsAreLoading, selectSubmissionsList } from 'state'
import { theme } from 'theme'
import styled from 'particles/themes/styled'
import PartiposterProfile from '../PartiposterProfile'
import ProgressBlock from './ProgressBlock'
import ReviewTable from './ReviewTable'
import { STATE_KEYS } from 'state/requests'
import Verify from './Verify'
import VerifyAllTasks from './VerifyAllTasks'
import MassSubmissionCheckbox from '../MassSubmissionCheckbox'
import { requestProfileOptions } from 'state/constants'
import { STEP_TYPE_ENUM } from 'services/api/constants'
import { EmptyStateWrapper, SubmissionsWrapper } from './styles'
import { isAllowedReviewStatus, isNoneSubmitBlockExisted } from 'components/utils/submission'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import { clearSubmissionFilters } from 'state/filter'

interface SubmissionContainerProps {
    isBulkActionErr?: boolean
}

export const SubmissionContainer = styled(Flex)<SubmissionContainerProps>`
    background: ${theme.newColors.neutral50};
    /* Disable - light 3 */

    border: 1px solid ${props => (props.isBulkActionErr ? theme.colors.frenchRose : theme.colors.iron)};
    box-sizing: border-box;
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.25);
    border-radius: 2px;
`

const Cell: React.FC<GridChildComponentProps> = memo(({ columnIndex, rowIndex, style, data }) => {
    const { submissions, setRowHeight, isRowColumnMaxHeight } = data
    const index = columnIndex + rowIndex * 3
    const submission = index < submissions.length ? submissions[index] : {}
    const sidebarStep = useSelector(selectSidebarStep)
    const sidebarTask = useSelector(selectSidebarTask)
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const checkCanReviewSubmission = useSelector(selectCheckCanReviewSubmissionByStep)
    const campaignSummary = useSelector(selectSelectedCampaignSummary)
    const hasResubmitted = submissions.some((submission: SubmissionProgressProps) => submission?.resubmitted === true)

    const rowRef = useRef<any>({})

    useEffect(() => {
        if (rowRef.current) {
            setRowHeight(rowIndex, columnIndex, rowRef.current.clientHeight)
        }
    }, [rowRef, rowRef.current && rowRef.current.clientHeight])
    const isBulkActionErr = submission && submission.error ? true : false

    const getCardCheckBox = () => {
        const ableToReview =
            isAllowedReviewStatus(selectedSubmissionView) && sidebarStep && checkCanReviewSubmission(sidebarStep.type)
        const isCampaignEnded = campaignSummary?.status === CAMPAIGN_STATUS_ENUM.CLOSED
        if (ableToReview && !isCampaignEnded) {
            return <MassSubmissionCheckbox submission={submission} />
        }
        return null
    }

    if (!sidebarStep || !sidebarTask) {
        return null
    }

    return (
        <Box style={style} key={submission.uuid} mb={theme.spacing.xxxs}>
            {!_.isEmpty(submission) ? (
                <SubmissionContainer
                    m={theme.spacing.xxxs}
                    flexDirection="column"
                    height="-webkit-fill-available"
                    isBulkActionErr={isBulkActionErr}
                    sx={{
                        textAlign: 'center',
                        boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
                        borderRadius: '4px',
                    }}
                >
                    <Box
                        ref={rowRef}
                        p={theme.spacing.xxs}
                        {...(!isRowColumnMaxHeight(rowIndex, columnIndex) && {
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                        })}
                    >
                        <PartiposterProfile
                            submission={submission}
                            submissionUuid={submission.submission_uuid}
                            options={{ instagram: true, facebook: sidebarTask.type === 'facebook_post' }}
                            TopRightComponent={getCardCheckBox()}
                        />
                        {isBulkActionErr && (
                            <Text fontSize="0.8em" textAlign="left" color={theme.colors.frenchRose} my="6px">
                                {submission.error}
                            </Text>
                        )}
                        <ProgressBlock
                            submissionProgress={submission}
                            step={sidebarStep}
                            task={sidebarTask}
                            hasResubmitted={hasResubmitted}
                        />
                    </Box>
                </SubmissionContainer>
            ) : (
                <></>
            )}
        </Box>
    )
}, areEqual)
Cell.displayName = 'Cell'

const COLUMN_COUNT = 3

interface SubmissionProps {
    submissions: SubmissionState[]
}

const SubmissionCards: React.FC<SubmissionProps> = ({ submissions }) => {
    const rowHeights = useRef<any>({})
    const gridRef = useRef<any>({})
    const maxHeightRowColumns = useRef<any>({})

    const getRowHeight = (index: number): number => {
        return rowHeights.current[index] || 650
    }

    const setRowHeight = (rowIndex: number, columnIndex: number, size: number): void => {
        const currentSize = rowHeights.current[rowIndex] ? rowHeights.current[rowIndex] : 0
        if (size >= currentSize + 15) {
            gridRef.current.resetAfterRowIndex(0)
            if (maxHeightRowColumns.current[rowIndex]) {
                Object.keys(maxHeightRowColumns.current[rowIndex]).forEach(key => {
                    maxHeightRowColumns.current[rowIndex][key] = false
                })
            }
            maxHeightRowColumns.current[rowIndex] = { ...maxHeightRowColumns.current[rowIndex], [columnIndex]: true }
            rowHeights.current = { ...rowHeights.current, [rowIndex]: Math.max(currentSize, size) }
        }
    }

    const isRowColumnMaxHeight = (rowIndex: number, columnIndex: number): boolean => {
        try {
            return maxHeightRowColumns.current[rowIndex][columnIndex]
        } catch {
            return true
        }
    }

    return (
        <SubmissionsWrapper>
            <AutoSizer>
                {({ height, width }) => {
                    return (
                        <Grid
                            ref={gridRef}
                            columnCount={COLUMN_COUNT}
                            rowCount={Math.ceil(submissions.length / COLUMN_COUNT)}
                            height={height}
                            width={width}
                            columnWidth={(): number => (width - 30) / COLUMN_COUNT}
                            rowHeight={index => getRowHeight(index) + 15}
                            itemData={{
                                submissions,
                                setRowHeight,
                                isRowColumnMaxHeight,
                            }}
                        >
                            {Cell}
                        </Grid>
                    )
                }}
            </AutoSizer>
        </SubmissionsWrapper>
    )
}

const SubmissionList: React.FC<any> = props => {
    const dispatch = useDispatch()

    const submissions = useSelector(selectSubmissionsList)
    const batchSubmissions = useSelector(selectBatchSubmissionsList)
    const selectedCampaignUuid = useSelector(selectSelectedCampaignUuid)
    const selectedCampaign = useSelector(selectSelectedCampaign)
    const sidebarStep = useSelector(selectSidebarStep)
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const submissionListParams = useSelector(selectSubmissionListParams)
    const activePageNum = useSelector(selectActivePageNum)
    const reviewPendingSubmission = useSelector(selectReviewPendingSubmission)
    const isPendingSubmissionExist = useSelector(selectIsPendingSubmitSubmissionExist)
    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.SubmissionsList]))

    useEffect(() => {
        if (selectedCampaign) dispatch(requestProfileOptions({ country: selectedCampaign.country }))
    }, [selectedCampaign])

    useEffect(() => {
        const offset: number = activePageNum * MAX_PAGE_SIZE
        if (selectedCampaignUuid && sidebarStep) {
            if (selectedSubmissionView !== SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS) {
                dispatch(
                    getSubmissionsPageList({
                        offset,
                        campaignUuid: selectedCampaignUuid,
                        stepUuid: sidebarStep.uuid,
                        ...submissionListParams,
                    }),
                )
            } else {
                dispatch(
                    getBatchSubmissionsPageList({
                        offset,
                        campaignUuid: selectedCampaignUuid,
                        ...submissionListParams,
                    }),
                )
            }
        }

        return () => {
            dispatch(clearSubmissionsList())
            dispatch(clearSubmissionFilters())
            // calling the cancel action to prevent the display of previous submission data on the dropout/reject page if the user swiftly switches to that page.
            dispatch(cancelSubmissionPageList())
            dispatch(cancelBatchSubmissionPageList())
        }
    }, [
        selectedCampaignUuid,
        sidebarStep,
        selectedSubmissionView,
        submissionListParams.tags,
        submissionListParams.keywords,
        submissionListParams.vote_type,
        submissionListParams.resubmitted,
        activePageNum,
        reviewPendingSubmission,
    ])
    if (sidebarStep && isShowCardReviewComponent(sidebarStep.type) && isLoading) {
        return null
    }
    if (
        (selectedSubmissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS &&
            (batchSubmissions.length === 0 || (!isPendingSubmissionExist && reviewPendingSubmission != null))) ||
        (selectedSubmissionView !== SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS &&
            sidebarStep &&
            isShowCardReviewComponent(sidebarStep.type) &&
            submissions.length === 0) ||
        !sidebarStep
    ) {
        return (
            <EmptyStateWrapper>
                <EmptyState />
            </EmptyStateWrapper>
        )
    }

    if (selectedSubmissionView == SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS) {
        return reviewPendingSubmission ? (
            <VerifyAllTasks submissions={batchSubmissions} {...props} />
        ) : (
            <ReviewAllTasks submissions={batchSubmissions} {...props} />
        )
    } else {
        switch (sidebarStep.type) {
            case STEP_TYPE_ENUM.PURCHASE_PRODUCT:
            case STEP_TYPE_ENUM.ATTEND_EVENT:
            case STEP_TYPE_ENUM.SUBMIT_PROOF_FB_URL:
            case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_FACEBOOK_POST_URL:
            case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_FACEBOOK_POST:
            case STEP_TYPE_ENUM.SUBMIT_PROOF_IG_URL:
            case STEP_TYPE_ENUM.SUBMIT_PROOF_IG_SCREENSHOT:
            case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_SCREENSHOT:
            case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_INSTAGRAM_POST_URL:
            case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_POST:
            case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_TIKTOK_POST_URL:
            case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_TIKTOK_POST:
            case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_INSTAGRAM_REEL_URL:
            case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_INSTAGRAM_REEL:
            case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_TWITTER_POST:
            case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_YOUTUBE_VIDEO:
            case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_XIAOHONGSHU_POST_URL:
            case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_XIAOHONGSHU_POST:
            case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_OTHERS:
            case STEP_TYPE_ENUM.CUSTOM:
                const isNoneSubmitCustomStep =
                    sidebarStep.type === STEP_TYPE_ENUM.CUSTOM && isNoneSubmitBlockExisted(sidebarStep)

                if (sidebarStep.type !== STEP_TYPE_ENUM.CUSTOM || isNoneSubmitCustomStep) {
                    return <ReviewTable submissions={submissions} {...props} />
                }
        }
        return reviewPendingSubmission ? (
            <Verify submissions={submissions} {...props} />
        ) : (
            <SubmissionCards submissions={submissions} {...props} />
        )
    }
}

export default SubmissionList
